import { AcornPlaceholder, absMediaUrl } from "@/components/AcornPlaceholder";
import { GlassWater } from "lucide-react";

const CATEGORY_LABEL = {
    distillato: "Distillato",
    liquore: "Liquore",
    birra: "Birra",
    altro: "Altro",
};

/**
 * SpiritCard.
 * Fase 6.8: prezzo listino pubblico sempre visibile (anche anonimi).
 * Fase 8.9j-P1: giacenze staff mostrabili/nascondibili via prop `showStock`.
 */
export default function SpiritCard({ item, onOpen, showStock = true }) {
    const prices = (item.formats || [])
        .map((f) => (typeof f.price === "number" ? f.price : null))
        .filter((p) => p !== null && p > 0);
    const hasPrices = prices.length > 0;
    const minP = hasPrices ? Math.min(...prices) : null;

    const thumbUrl = absMediaUrl(item.images?.thumb_url);
    const cardUrl = absMediaUrl(item.images?.card_url);
    const displayUrl = thumbUrl || cardUrl;

    const stockRow = showStock && (item.formats || []).some((f) => typeof f.stock_quantity === "number")
        ? item.formats.map((f) => `${f.size} L · ${f.stock_quantity ?? "—"}`).join(" · ")
        : null;

    return (
        <button
            onClick={onOpen}
            data-testid={`spirit-card-${item.id}`}
            className="group co-card text-left p-3 sm:p-6 flex gap-3 sm:gap-6 hover:border-[hsl(var(--primary))] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 w-full"
        >
            <div className="shrink-0 flex items-start pt-1 w-12 sm:w-20">
                {displayUrl ? (
                    <img
                        src={displayUrl}
                        alt={item.name}
                        loading="lazy"
                        className="max-h-24 sm:max-h-32 max-w-full object-contain"
                    />
                ) : (
                    <AcornPlaceholder size={48} opacity={0.55} className="sm:!size-[72px]" />
                )}
            </div>

            <div className="min-w-0 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="co-caption">
                        {CATEGORY_LABEL[item.categoria] || "Distillato"}
                    </span>
                    {item.glass_available && (
                        <span className="co-badge-calice">
                            <GlassWater className="w-3 h-3" strokeWidth={1.5} /> Al Calice
                        </span>
                    )}
                </div>

                {/* FIX 8.9j-UI · gerarchia tipografica invertita: producer dominante */}
                <h3 className="text-xs sm:text-sm leading-tight text-[hsl(var(--foreground))] mb-0.5 sm:mb-1">
                    {item.name}
                </h3>

                {item.producer && item.producer !== "—" && (
                    <p className="text-lg sm:text-2xl leading-tight text-[hsl(var(--muted-foreground))] mb-1 sm:mb-2 truncate">
                        {item.producer}
                    </p>
                )}

                {item.organoleptic?.description && (
                    <p className="hidden sm:block text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mb-2">
                        {item.organoleptic.description}
                    </p>
                )}

                <div className="mt-auto pt-2 sm:pt-4 flex items-end justify-between gap-3">
                    <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-[hsl(var(--muted-foreground))]">
                        <span>{item.formats.map((f) => `${f.size} L`).join(" · ") || "—"}</span>
                    </div>

                    <div className="text-right">
                        <div className="co-caption mb-0.5 hidden sm:block">
                            {hasPrices && prices.length > 1 ? "Da" : ""}
                        </div>
                        <div
                            className={`co-price ${!hasPrices ? "text-[hsl(var(--muted-foreground))]" : ""}`}
                            data-testid={`spirit-card-price-${item.id}`}
                        >
                            {hasPrices ? `€ ${minP.toFixed(2)}` : "—"}
                        </div>
                    </div>
                </div>

                {stockRow && (
                    <div
                        className="mt-2 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]"
                        data-testid={`spirit-card-stock-${item.id}`}
                    >
                        Giacenza: {stockRow}
                    </div>
                )}
            </div>
        </button>
    );
}
